/**
 * NewVersionPage Component
 *
 * Full-page form for creating a new version of an existing dataset. Allows the
 * owner to deprecate the current version (with a required reason), carry forward
 * files from the previous version, and upload new/replacement files.
 *
 * Route: /datasets/:datasetId/new-version
 */

import { useState, useCallback, useMemo, type JSX } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { Upload, X, File, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useFileUpload } from "@/hooks/use-file-upload"
import {
  getDatasets,
  createNewVersion,
  type Dataset,
  type NewVersionRequest,
  type CarriedForwardFile,
} from "@/services/datasetsService"
import { useEffect } from "react"

const PAGE_TITLE = "Upload New Version"

/**
 * Page component for creating a new version of a dataset.
 *
 * Loads the dataset by ID from the URL params, displays the carry-forward file
 * list (from the current version), a deprecation reason textarea, and a file
 * upload dropzone for new files. On submit, orchestrates the multi-step version
 * creation flow via the datasets service.
 *
 * @returns The rendered new version form page.
 */
export default function NewVersionPage(): JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  // Dataset loading state
  const [dataset, setDataset] = useState<Dataset | null>(null)
  const [loadingDataset, setLoadingDataset] = useState<boolean>(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form state
  const [deprecationReason, setDeprecationReason] = useState<string>("")
  const [carriedForward, setCarriedForward] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // File upload for new files
  const [
    { files: newFiles, isDragging },
    {
      openFileDialog,
      removeFile,
      getInputProps,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    },
  ] = useFileUpload({
    accept: "*",
    multiple: true,
  })

  /**
   * Load the dataset on mount to get the current version's file list.
   */
  useEffect(() => {
    async function loadDataset(): Promise<void> {
      if (!datasetId) {
        setLoadError("No dataset ID provided")
        setLoadingDataset(false)
        return
      }

      try {
        const token = await getAccessToken()
        const datasets = await getDatasets(token)
        const found = datasets.find((d) => d.datasetId === datasetId)

        if (!found) {
          setLoadError(`Dataset '${datasetId}' not found`)
          setLoadingDataset(false)
          return
        }

        setDataset(found)
        // Initialize carried-forward with all current version files checked
        const currentVersionData = found.versions[String(found.currentVersion)]
        if (currentVersionData) {
          setCarriedForward(new Set(currentVersionData.fileKeys))
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : "Failed to load dataset")
      } finally {
        setLoadingDataset(false)
      }
    }

    loadDataset()
  }, [datasetId, getAccessToken])

  /** File keys for the current (soon-to-be-deprecated) version. */
  const currentVersionFileKeys: string[] = useMemo(() => {
    if (!dataset) return []
    const versionData = dataset.versions[String(dataset.currentVersion)]
    return versionData?.fileKeys ?? []
  }, [dataset])

  /** File keys being uploaded fresh (derived from the file upload state). */
  const newFileKeys: string[] = useMemo(() => {
    return newFiles.map((f) => (f.file instanceof window.File ? f.file.name : f.file.name))
  }, [newFiles])

  /** The combined file list for the new version (carried forward + new uploads). */
  const combinedFileKeys: string[] = useMemo(() => {
    return [...Array.from(carriedForward), ...newFileKeys]
  }, [carriedForward, newFileKeys])

  /** Whether there's a conflict between carried-forward and new file names. */
  const fileConflicts: string[] = useMemo(() => {
    const newSet = new Set(newFileKeys)
    return Array.from(carriedForward).filter((fk) => newSet.has(fk))
  }, [carriedForward, newFileKeys])

  /**
   * Toggle a file key in the carried-forward set.
   *
   * @param fileKey - The file key to toggle.
   * @param checked - Whether the file should be carried forward.
   */
  const toggleCarriedForward = useCallback((fileKey: string, checked: boolean): void => {
    setCarriedForward((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(fileKey)
      } else {
        next.delete(fileKey)
      }
      return next
    })
  }, [])

  /**
   * Handle form submission. Validates inputs and calls the createNewVersion
   * service function to orchestrate the multi-step flow.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!dataset || !datasetId) return

    // Validation
    if (!deprecationReason.trim()) {
      setSubmitError("Please provide a reason for deprecating the current version.")
      return
    }

    if (combinedFileKeys.length === 0) {
      setSubmitError("The new version must have at least one file.")
      return
    }

    if (fileConflicts.length > 0) {
      setSubmitError(
        `File name conflict: ${fileConflicts.join(", ")} appear in both carried-forward ` +
        `and new uploads. Uncheck the carried-forward file(s) to replace them with the new upload.`
      )
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    try {
      const token = await getAccessToken()

      // Build carried-forward entries with fromVersion = currentVersion
      const carriedForwardFiles: CarriedForwardFile[] = Array.from(carriedForward).map(
        (fileKey) => ({ fileKey, fromVersion: dataset.currentVersion })
      )

      const request: NewVersionRequest = {
        deprecationReason: deprecationReason.trim(),
        newFileKeys: newFileKeys,
        carriedForwardFiles: carriedForwardFiles,
      }

      // Extract actual File objects for upload
      const fileObjects: File[] = newFiles
        .map((f) => f.file)
        .filter((f): f is File => f instanceof window.File)

      await createNewVersion(token, datasetId, request, fileObjects)

      // Success — navigate back to the dataset
      navigate(`/datasets/${datasetId}`)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to create new version")
    } finally {
      setSubmitting(false)
    }
  }, [
    dataset,
    datasetId,
    deprecationReason,
    combinedFileKeys,
    fileConflicts,
    newFileKeys,
    carriedForward,
    newFiles,
    getAccessToken,
    navigate,
  ])

  // Loading state
  if (loadingDataset) {
    return (
      <>
        <title>{PAGE_TITLE} - MAP Apps Platform</title>
        <div className="flex items-center justify-center flex-1">
          <p className="text-muted-foreground">Loading dataset...</p>
        </div>
      </>
    )
  }

  // Error state
  if (loadError || !dataset) {
    return (
      <>
        <title>{PAGE_TITLE} - MAP Apps Platform</title>
        <div className="flex items-center justify-center flex-1">
          <p className="text-destructive">{loadError ?? "Dataset not found"}</p>
        </div>
      </>
    )
  }

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-6">
        <Card className="max-w-2xl w-full mx-auto shadow-none">
          <CardHeader className="pb-4">
            <CardTitle><h1>{PAGE_TITLE}</h1></CardTitle>
            <CardDescription>
              Create a new version of &ldquo;{dataset.title}&rdquo; (currently version {dataset.currentVersion}).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Warning banner */}
            <div className="flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/50">
              <AlertTriangle className="size-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium">This is for correcting errors only</p>
                <p className="mt-1 text-amber-700 dark:text-amber-300">
                  If you are uploading a new year&apos;s data or an entirely new collection,
                  create a new dataset instead. Versioning is for fixing mistakes in
                  existing data.
                </p>
              </div>
            </div>

            {/* Deprecation reason */}
            <div className="grid gap-1.5">
              <label htmlFor="deprecation-reason" className="text-sm font-medium">
                What was wrong with the previous version? <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="deprecation-reason"
                placeholder="e.g. Incorrect coordinates in file2.csv, data was from the wrong survey year"
                value={deprecationReason}
                onChange={(e) => setDeprecationReason(e.target.value)}
                rows={3}
              />
            </div>

            {/* Carry forward files */}
            <div className="grid gap-1.5">
              <label className="text-sm font-medium">
                Files from version {dataset.currentVersion}
              </label>
              <p className="text-xs text-muted-foreground">
                Checked files will be carried forward to the new version. Uncheck files
                you want to remove or replace with a new upload.
              </p>
              <ul className="space-y-1.5 rounded-md border border-input bg-muted/50 p-3">
                {currentVersionFileKeys.map((fileKey) => (
                  <li key={fileKey} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      id={`carry-${fileKey}`}
                      checked={carriedForward.has(fileKey)}
                      onCheckedChange={(checked) =>
                        toggleCarriedForward(fileKey, checked === true)
                      }
                    />
                    <File className="size-3.5 text-muted-foreground shrink-0" />
                    <label htmlFor={`carry-${fileKey}`} className="truncate cursor-pointer">
                      {fileKey}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* New file uploads */}
            <div className="grid gap-1.5">
              <label className="text-sm font-medium">
                Upload new or replacement files
              </label>
              <p className="text-xs text-muted-foreground">
                These files will be added to the new version. If a file has the same name
                as a carried-forward file, uncheck the carried-forward file above first.
              </p>
              <div
                role="button"
                tabIndex={0}
                onClick={openFileDialog}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    openFileDialog()
                  }
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                data-dragging={isDragging || undefined}
                className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input p-6 text-center cursor-pointer transition-colors hover:bg-accent/50 data-[dragging=true]:border-primary data-[dragging=true]:bg-accent"
              >
                <Upload className="size-5 text-muted-foreground" />
                <p className="text-sm">
                  <span className="font-medium">Choose file(s)</span> or drag and drop here
                </p>
              </div>
              <input {...getInputProps()} className="sr-only" />
              {newFiles.length > 0 && (
                <ul className="space-y-1">
                  {newFiles.map((file) => (
                    <li key={file.id} className="flex items-center gap-2 text-sm">
                      <File className="size-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">
                        {file.file instanceof window.File ? file.file.name : file.file.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="size-5 p-0"
                        onClick={() => removeFile(file.id)}
                        aria-label={`Remove ${file.file instanceof window.File ? file.file.name : file.file.name}`}
                      >
                        <X className="size-3" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* File conflicts warning */}
            {fileConflicts.length > 0 && (
              <p className="text-sm text-destructive" role="alert">
                File name conflict: {fileConflicts.join(", ")} appear in both carried-forward
                and new uploads. Uncheck the carried-forward file(s) above to replace them.
              </p>
            )}

            {/* Summary */}
            <div className="rounded-md border border-input bg-muted/30 p-3">
              <p className="text-sm text-muted-foreground">
                New version will contain <span className="font-medium text-foreground">{combinedFileKeys.length}</span> file(s):
                {" "}{Array.from(carriedForward).length} carried forward
                {newFileKeys.length > 0 && <>, {newFileKeys.length} new</>}.
              </p>
            </div>

            {/* Submit error */}
            {submitError && (
              <p className="text-sm text-destructive" role="alert">
                {submitError}
              </p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/datasets/${datasetId}`)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || combinedFileKeys.length === 0 || fileConflicts.length > 0}
              >
                {submitting ? "Creating version…" : "Create New Version"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
